import * as React from "react";
import { Link, useLocation } from "react-router-dom";

import { Tooltip } from "Components/ui";
import { isNavItemActive, NAVIGATION, type NavGroup } from "config/navigation";
import { useAuth } from "hooks/useAuth";
import { cn } from "utils/cn";

/** Navigation groups the signed-in user can see (items without permission are dropped). */
export const useVisibleNavigation = (): NavGroup[] => {
  const { hasPermission } = useAuth();
  return React.useMemo(
    () =>
      NAVIGATION.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            !item.permission || hasPermission(item.permission.resource, item.permission.action)
        ),
      })).filter((group) => group.items.length > 0),
    [hasPermission]
  );
};

interface SidebarNavProps {
  collapsed?: boolean;
  /** Called after a link is chosen (closes the mobile drawer). */
  onNavigate?: () => void;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({ collapsed = false, onNavigate }) => {
  const { pathname } = useLocation();
  const groups = useVisibleNavigation();

  return (
    <nav aria-label="Main" className="flex flex-col gap-6">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-0.5">
          {collapsed ? (
            <span aria-hidden="true" className="mx-auto mb-1 h-px w-5 bg-border first:hidden" />
          ) : (
            <p className="mb-1.5 px-2.5 text-xs font-medium text-subtle-foreground">
              {group.label}
            </p>
          )}
          {group.items.map((item) => {
            const active = isNavItemActive(item, pathname);
            const Icon = item.icon;
            const link = (
              <Link
                to={item.path}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group flex h-9 items-center gap-3 rounded-control px-2.5 text-sm transition-[background-color,color,box-shadow] duration-fast",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  collapsed && "justify-center px-0",
                  // The current page is a raised chip on the sidebar ground.
                  active
                    ? "bg-surface font-medium text-foreground shadow-xs ring-1 ring-border/70"
                    : "text-muted-foreground hover:bg-surface/60 hover:text-foreground"
                )}
              >
                <Icon
                  weight="duotone"
                  className={cn(
                    "h-[18px] w-[18px] shrink-0",
                    active
                      ? "text-primary-text"
                      : "text-subtle-foreground group-hover:text-muted-foreground"
                  )}
                />
                {!collapsed && <span className="truncate">{item.label}</span>}
                {collapsed && <span className="sr-only">{item.label}</span>}
              </Link>
            );
            return collapsed ? (
              <Tooltip key={item.path} content={item.label} side="right">
                {link}
              </Tooltip>
            ) : (
              <React.Fragment key={item.path}>{link}</React.Fragment>
            );
          })}
        </div>
      ))}
    </nav>
  );
};
